import { BankAccount, BankAccountSchema } from '@40seas/entities/bank-account';
import { BusinessSchema } from '@40seas/entities/businesses';
import { Buyer } from '@40seas/entities/buyers';
import { Charge, ChargeSchema } from '@40seas/entities/charges';
import { Checkout, CheckoutSchema } from '@40seas/entities/checkout';
import {
    EntityReferenceWithAmount,
    EntityReferenceWithAmountSchema,
    EnumSchema,
    Executor,
    ExecutorSchema,
    FromSchema,
    ZodDate,
    ZodObjectId,
} from '@40seas/entities/common';
import { CurrencyExchange, CurrencyExchangeSchema } from '@40seas/entities/currency-exchange';
import { Invoice, InvoiceSchema } from '@40seas/entities/invoices';
import { Seller } from '@40seas/entities/sellers';
import { BaseEntity, CalculatedFee, CalculatedFeeSchema, Currency, PaymentMethodType, WithBaseEntitySchema } from '@40seas/models';
import { z } from 'zod';
import { LinkedPayoutReason } from './linked-payout-reason.enum';
import { PayoutType } from './payout-type.enum';
import { PayoutStatus } from './payout.status.enum';

export const LinkedPayoutSchema = z
    .object({
        payout: z
            .string()
            .or(z.lazy(() => PayoutSchema))
            .optional(),
        reason: EnumSchema(LinkedPayoutReason).optional(),
    })
    .openapi({ ref: 'LinkedPayout' })
    .strict();

export const LinkedPayoutRefSchema = z
    .object({
        payout: z.string(), // Reference to another payout by ID
        reason: EnumSchema(LinkedPayoutReason).optional(),
    })
    .openapi({ ref: 'LinkedPayoutRef' })
    .strict();

export class LinkedPayout extends FromSchema(LinkedPayoutSchema) {
    payout?: string | Payout;
    reason?: LinkedPayoutReason;
}

export const PayoutSchema = WithBaseEntitySchema(
    z.object({
        buyer: ZodObjectId.or(BusinessSchema).optional(),
        amount: z.number().gt(0, 'Payout amount must be greater than 0'),
        netAmount: z.number().optional(),
        grossAmount: z.number().optional(),
        paymentMethod: EnumSchema(PaymentMethodType),
        currency: EnumSchema(Currency),
        status: EnumSchema(PayoutStatus),
        type: EnumSchema(PayoutType),
        invoice: ZodObjectId.or(InvoiceSchema).optional(),
        invoices: z.array(EntityReferenceWithAmountSchema).optional(),
        checkout: ZodObjectId.or(CheckoutSchema).optional(),
        charge: ZodObjectId.or(ChargeSchema).optional(),
        seller: ZodObjectId.or(BusinessSchema),
        initiatedDate: ZodDate.optional(),
        paidDate: ZodDate.optional(),
        bankAccount: ZodObjectId.or(BankAccountSchema).optional(),
        feeAmount: z.number().optional(),
        fees: z.array(CalculatedFeeSchema).optional(),
        linkedPayouts: z
            .array(LinkedPayoutSchema)
            .optional()
            .openapi({
                description: 'A list of linked payouts represented as references in OpenAPI to avoid recursion.',
                items: { $ref: '#/components/schemas/LinkedPayout' },
            }),
        balance: z.number().optional(),
        currencyExchange: CurrencyExchangeSchema.optional(),
        createdBy: ExecutorSchema.optional(),
        metadata: z.record(z.string()).optional(),
        timeline: z.record(z.object({ updatedAt: ZodDate })).optional(),
    }),
).strict();

export class Payout extends FromSchema(PayoutSchema) implements BaseEntity {
    id: string;
    buyer?: string | Buyer;
    amount: number;
    netAmount?: number;
    grossAmount?: number;
    paymentMethod: PaymentMethodType;
    currency: Currency;
    status: PayoutStatus;
    type: PayoutType;
    invoice?: string | Invoice;
    invoices?: EntityReferenceWithAmount[];
    checkout?: string | Checkout;
    charge?: string | Charge;
    seller: string | Seller;
    initiatedDate?: Date;
    paidDate?: Date;
    bankAccount?: string | BankAccount;
    feeAmount?: number;
    fees?: CalculatedFee[];
    linkedPayouts?: LinkedPayout[];
    balance?: number;
    currencyExchange?: CurrencyExchange;
    createdAt: Date;
    updatedAt: Date;
    tenantId: string;
    createdBy?: Executor;
    metadata?: { [key: string]: string };
    timeline?: { [status: string]: { updatedAt: Date } };
}
